import React, { useCallback, useEffect, useState } from 'react';
import { useFocusEffect } from '@react-navigation/core';
import Layout from '../../../components/Layout';
import {
  AppNavigationProp,
  NavigationParamList,
  ScreenName,
  StackNavigationScreenProps,
} from '../../../navigation/types';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import {
  AdminHelpdeskIssueOrderBy,
  AdminPostRead,
  GetAdminHelpdeskIssuesParams,
  Order,
  Pagination as IPagination,
  Role,
} from '../../../client/interfaces';
import { HelpdeskIssueFilter, HelpdeskIssueTable, Button, Pagination } from '../../../components';
import { useMessage } from '../../../contexts/Messages/MessageContext';
import { useAdminHelpdesk } from '../../../contexts/Admin/AdminHelpdeskContext';
import { useAppTranslation } from '../../../hooks/useAppTranslation';
import client from '../../../client/client';
import { NavigationContextStack } from '@/contexts/NavigationContext/NavigationContextStack';

export const emptyGetAdminHelpdeskIssuesParams: GetAdminHelpdeskIssuesParams = {
  orderBy: AdminHelpdeskIssueOrderBy.createdAtUtc,
  order: Order.DESC,
  search: '',
  topics: [],
  organizations: [],
  page: 1,
  status: undefined,
};

const AdminHelpdeskIssueListScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.AdminHelpdeskIssueListScreen>
> = ({ navigation: nav, route }) => {
  const { t } = useAppTranslation();
  const { me, hasRole } = useAuth();
  const navigation = nav as AppNavigationProp;
  const { setMessage } = useMessage();
  const { refreshTopicsAndOrganizations } = useAdminHelpdesk();

  useEffect(() => {
    refreshTopicsAndOrganizations();
  }, []);
  const { topics, organizations } = useAdminHelpdesk();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getAdminHelpdeskIssuesParams, setGetAdminHelpdeskIssuesParams] =
    useState<GetAdminHelpdeskIssuesParams>({
      ...emptyGetAdminHelpdeskIssuesParams,
      ...route.params,
    });

  const [helpdeskIssues, setHelpdeskIssues] = useState<AdminPostRead[]>([]);
  const [pagination, setPagination] = useState<IPagination>();

  const getAdminHelpdeskIssuesParamsJSON = JSON.stringify(getAdminHelpdeskIssuesParams);

  // Role guard
  useFocusEffect(
    useCallback(() => {
      if (me && !hasRole(Role.helpdeskIssueAdmin)) {
        navigation.navigate(ScreenName.HomeScreen);
      } else {
        setGetAdminHelpdeskIssuesParams(emptyGetAdminHelpdeskIssuesParams);
      }
    }, [me?.roleList]),
  );

  const fetchHelpdeskIssues = async () => {
    setIsLoading(true);
    try {
      const response = await client.getAdminPaginatedHelpdeskIssues(getAdminHelpdeskIssuesParams);
      setHelpdeskIssues(response.items);
      setPagination(response.pagination);
    } catch (e) {
      console.error(e);
      setMessage({ message: e.message, type: 'error' });
    }
    setIsLoading(false);
  };

  useFocusEffect(
    useCallback(() => {
      fetchHelpdeskIssues();
    }, [getAdminHelpdeskIssuesParamsJSON]),
  );

  useEffect(() => {
    setGetAdminHelpdeskIssuesParams((prev) => ({ ...prev, ...route.params }));
  }, [route.params]);

  return (
    <Layout title={t('admin:helpdesk_issues_list:title')} titleMarginBottom={false}>
      <Button
        text={t('admin:helpdesk_issues_list:add_new')}
        variant={'fill'}
        onPress={() => {
          navigation.navigate(ScreenName.AdminHelpdeskIssueEditScreen);
        }}
        size={'medium'}
        style={{ alignItems: 'flex-end', marginBottom: 40 }}
      />
      <HelpdeskIssueFilter
        params={getAdminHelpdeskIssuesParams}
        paramSetter={setGetAdminHelpdeskIssuesParams}
        pagination={pagination}
        topics={topics}
        organizations={organizations}
      />
      <HelpdeskIssueTable
        rows={helpdeskIssues}
        paramSetter={setGetAdminHelpdeskIssuesParams}
        params={getAdminHelpdeskIssuesParams}
        refresh={fetchHelpdeskIssues}
        isLoading={isLoading}
      />
      {pagination && (
        <Pagination
          data={pagination}
          paramSetter={setGetAdminHelpdeskIssuesParams}
          params={getAdminHelpdeskIssuesParams}
        />
      )}
    </Layout>
  );
};

export default AdminHelpdeskIssueListScreen;
